import React, { useState, useEffect, useRef } from 'react';
import axios from 'src/api/axios';

const CountrySelect = ({ value, onChange, name, className = 'bg-white font-medium border border-gray-300 text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px]' }) => {
  const [countries, setCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get('api/resources/countries');
        setCountries(response.data.data);
        
        // Set initial selected country if value prop exists
        if (value) {
          const country = response.data.data.find(c => c.cca2 === value);
          if (country) {
            setSelectedCountry(country);
            setSearchTerm(country.name);
          }
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (Array.isArray(country.altSpellings) && country.altSpellings.some(alt => alt.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const handleSelect = (country) => {
    setSelectedCountry(country);
    setSearchTerm(country.name);
    onChange({ target: { name: name, value: country.cca2 }});
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedCountry(null);
    setSearchTerm('');
    onChange({ target: { name: name, value: '' }});
    setIsOpen(false);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleArrowClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          name={name}
          placeholder="Search country..."
          value={searchTerm}
          onChange={handleInputChange}
          onClick={handleInputClick}
          className={`${className} pr-10`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {selectedCountry && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Clear country"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={handleArrowClick}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Toggle dropdown"
          >
            <svg 
              className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="max-h-60 overflow-y-auto">
            {filteredCountries.map(country => (
              <div
                key={country.id}
                onClick={() => handleSelect(country)}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer"
              >
                <img src={country.flags.svg} alt={country.flags.alt} className="w-6 h-4 object-cover" />
                <span>{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelect;